import StarterKitModule from '@tiptap/starter-kit';
import { Extension } from '@tiptap/core';
import ParagraphModule from '@tiptap/extension-paragraph';
import HeadingModule from '@tiptap/extension-heading';
import ListItemModule from '@tiptap/extension-list-item';
import BulletListModule from '@tiptap/extension-bullet-list';
import OrderedListModule from '@tiptap/extension-ordered-list';
import TaskListModule from '@tiptap/extension-task-list';
import TaskItemModule from '@tiptap/extension-task-item';
import BlockquoteModule from '@tiptap/extension-blockquote';
import CodeBlockModule from '@tiptap/extension-code-block';
import PlaceholderModule from '@tiptap/extension-placeholder';
import ImageModule from '@tiptap/extension-image';
import * as TableModule from '@tiptap/extension-table';
import * as TableRowModule from '@tiptap/extension-table-row';
import * as TableHeaderModule from '@tiptap/extension-table-header';
import * as TableCellModule from '@tiptap/extension-table-cell';
import { ChunkAttribute } from './chunkAttribute';
import ChatMessage from './chatMessage';
import { DiffSuggestionAddMark, DiffSuggestionRemoveMark } from './diffSuggestionMarks';
import config from '../config';

// Resolve potential Jest/CJS module wrappers that expose the real extension on
// the `.default` property. This keeps imports working both at runtime and in
// the Jest environment where we provide virtual module mocks.
const resolveModule = (mod) => (mod && mod.default) ? mod.default : mod;
const resolveNamedModule = (mod, exportName) => {
  if (!mod) return mod;
  const candidate = Object.prototype.hasOwnProperty.call(mod, exportName)
    ? mod[exportName]
    : mod;
  return resolveModule(candidate);
};

const addClassAttribute = () => ({
  class: {
    default: null,
    parseHTML: element => element.getAttribute('class'),
    renderHTML: attributes => {
      if (!attributes.class) {
        return {};
      }
      return {
        class: attributes.class,
      };
    },
  },
});

const addNodeAnchorIdAttribute = () => ({
  node_anchor_id: {
    default: null,
    parseHTML: element => element.getAttribute('data-node-anchor-id') || element.getAttribute('id') || null,
    renderHTML: attributes => {
      const value = String(attributes?.node_anchor_id || '').trim();
      if (!value) {
        return {};
      }
      return {
        id: value,
      };
    },
  },
});

// Adds diff-annotation attrs to structural nodes (table, tableRow) so that
// full-table and full-row deletion suggestions can be tracked and resolved
// without requiring inline marks on every text node.
const addDiffNodeAttrs = () => ({
  diffOp: {
    default: null,
    parseHTML: el => el.getAttribute('data-diff-op') || null,
    renderHTML: attrs => attrs.diffOp ? { 'data-diff-op': attrs.diffOp } : {}
  },
  diffAnnotationId: {
    default: null,
    parseHTML: el => el.getAttribute('data-diff-id') || null,
    renderHTML: attrs => attrs.diffAnnotationId ? { 'data-diff-id': attrs.diffAnnotationId } : {}
  },
  diffBatchId: {
    default: null,
    parseHTML: el => el.getAttribute('data-diff-batch-id') || null,
    renderHTML: attrs => attrs.diffBatchId ? { 'data-diff-batch-id': attrs.diffBatchId } : {}
  }
});

const TabHandler = Extension.create({
  name: 'tabHandler',

  addKeyboardShortcuts() {
    return {
      'Tab': () => {
        const { editor } = this;

        if (editor.isActive('taskList')) {
          return editor.commands.sinkListItem('taskItem');
        }

        if (editor.isActive('bulletList') || editor.isActive('orderedList')) {
          return editor.commands.sinkListItem('listItem');
        }

        const getIndent = () => {
          let attrs;
          if (editor.isActive('heading')) {
            attrs = editor.getAttributes('heading');
          } else {
            attrs = editor.getAttributes('paragraph');
          }
          const classes = attrs.class || '';
          const match = classes.match(/\bin([1-6])\b/);
          return match ? parseInt(match[1], 10) : 0;
        };

        const setIndent = (level) => {
          let nodeType = 'paragraph';
          if (editor.isActive('heading')) {
            nodeType = 'heading';
          }

          const attrs = editor.getAttributes(nodeType);
          const classes = attrs.class || '';
          const cleaned = classes.replace(/\bin[1-6]\b/g, '').trim();
          const updated = level > 0 ? `${cleaned} in${level}`.trim() : cleaned;

          editor.chain().focus().updateAttributes(nodeType, {
            class: updated || null
          }).run();
        };

        const current = getIndent();
        if (current < 6) {
          setIndent(current + 1);
          return true;
        }
        return false;
      },
      'Shift-Tab': () => {
        const { editor } = this;

        if (editor.isActive('taskList')) {
          return editor.commands.liftListItem('taskItem');
        }

        if (editor.isActive('bulletList') || editor.isActive('orderedList')) {
          return editor.commands.liftListItem('listItem');
        }

        const getIndent = () => {
          let attrs;
          if (editor.isActive('heading')) {
            attrs = editor.getAttributes('heading');
          } else {
            attrs = editor.getAttributes('paragraph');
          }
          const classes = attrs.class || '';
          const match = classes.match(/\bin([1-6])\b/);
          return match ? parseInt(match[1], 10) : 0;
        };

        const setIndent = (level) => {
          let nodeType = 'paragraph';
          if (editor.isActive('heading')) {
            nodeType = 'heading';
          }

          const attrs = editor.getAttributes(nodeType);
          const classes = attrs.class || '';
          const cleaned = classes.replace(/\bin[1-6]\b/g, '').trim();
          const updated = level > 0 ? `${cleaned} in${level}`.trim() : cleaned;

          editor.chain().focus().updateAttributes(nodeType, {
            class: updated || null
          }).run();
        };

        const current = getIndent();
        if (current > 0) {
          setIndent(current - 1);
          return true;
        }
        return false;
      },
    };
  },
});

// Resolve possibly-wrapped modules
const StarterKit = resolveModule(StarterKitModule);
const Paragraph = resolveModule(ParagraphModule);
const Heading = resolveModule(HeadingModule);
const ListItem = resolveModule(ListItemModule);
const BulletList = resolveModule(BulletListModule);
const OrderedList = resolveModule(OrderedListModule);
const TaskList = resolveModule(TaskListModule);
const TaskItem = resolveModule(TaskItemModule);
const Blockquote = resolveModule(BlockquoteModule);
const CodeBlock = resolveModule(CodeBlockModule);
const Placeholder = resolveModule(PlaceholderModule);
// Helper to safely configure an extension if it exposes a `configure` method
const configureExtension = (ext, opts) => {
  try {
    if (!ext) return ext;
    if (typeof ext.configure === 'function') return ext.configure(opts);
    if (ext && ext.default && typeof ext.default.configure === 'function') return ext.default.configure(opts);
    return ext;
  } catch (e) {
    return ext;
  }
};

// Resolve table-related modules with fallbacks for different module shapes
let Table = resolveNamedModule(TableModule, 'Table');
if (!Table || typeof Table.extend !== 'function') {
  const alt = resolveModule(TableModule);
  if (alt && typeof alt.extend === 'function') Table = alt;
}
let TableRow = resolveNamedModule(TableRowModule, 'TableRow');
if (!TableRow) TableRow = resolveModule(TableRowModule);
let TableHeader = resolveNamedModule(TableHeaderModule, 'TableHeader');
if (!TableHeader) TableHeader = resolveModule(TableHeaderModule);
let TableCell = resolveNamedModule(TableCellModule, 'TableCell');
if (!TableCell) TableCell = resolveModule(TableCellModule);

// Extend Table and TableRow with diff-annotation node attributes so that
// full-table and full-row deletion suggestions can be stored on the node itself.
let CustomTable = Table;
if (Table && typeof Table.extend === 'function') {
  CustomTable = Table.extend({
    addAttributes() {
      return { ...this.parent?.(), ...addDiffNodeAttrs() };
    }
  });
}

let CustomTableRow = TableRow;
if (TableRow && typeof TableRow.extend === 'function') {
  CustomTableRow = TableRow.extend({
    addAttributes() {
      return { ...this.parent?.(), ...addDiffNodeAttrs() };
    }
  });
}

let CustomTableHeader = TableHeader;
if (TableHeader && typeof TableHeader.extend === 'function') {
  CustomTableHeader = TableHeader.extend({
    addAttributes() {
      return { ...this.parent?.(), ...addDiffNodeAttrs() };
    }
  });
}

let CustomTableCell = TableCell;
if (TableCell && typeof TableCell.extend === 'function') {
  CustomTableCell = TableCell.extend({
    addAttributes() {
      return { ...this.parent?.(), ...addDiffNodeAttrs() };
    }
  });
}

const CustomParagraph = Paragraph.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      ...addClassAttribute(),
      ...addNodeAnchorIdAttribute(),
    };
  },
});

const CustomHeading = Heading.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      ...addClassAttribute(),
      ...addNodeAnchorIdAttribute(),
    };
  },
});

const CustomListItem = ListItem.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      ...addClassAttribute(),
      ...addNodeAnchorIdAttribute(),
    };
  },
});

const CustomBulletList = BulletList.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      ...addClassAttribute(),
      ...addNodeAnchorIdAttribute(),
    };
  },
});

const CustomOrderedList = OrderedList.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      ...addClassAttribute(),
      ...addNodeAnchorIdAttribute(),
    };
  },
});

const CustomTaskList = TaskList.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      ...addClassAttribute(),
      ...addNodeAnchorIdAttribute(),
    };
  },
});

const CustomTaskItem = TaskItem.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      ...addClassAttribute(),
      ...addNodeAnchorIdAttribute(),
    };
  },
});

const CustomBlockquote = Blockquote.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      ...addClassAttribute(),
      ...addNodeAnchorIdAttribute(),
    };
  },
});

const CustomCodeBlock = CodeBlock.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      ...addClassAttribute(),
      ...addNodeAnchorIdAttribute(),
    };
  },
});

const Image = resolveModule(ImageModule);

const MIN_IMAGE_WIDTH = Number(config?.IMAGE?.MIN_NODE_WIDTH || 48);
const DEFAULT_IMAGE_MAX_WIDTH = Number(config?.IMAGE?.MAX_NODE_WIDTH || 2000);

const createResizableImageNodeView = (node, view, getPos) => {
  let currentNode = node;
  const wrapper = document.createElement('span');
  wrapper.className = 'image-wrapper';

  const img = document.createElement('img');
  img.className = 'image-element';
  img.draggable = false;
  img.loading = 'lazy';
  wrapper.appendChild(img);

  const handle = document.createElement('span');
  handle.className = 'image-handle';
  handle.setAttribute('aria-hidden', 'true');
  const icon = document.createElement('i');
  icon.className = 'icon notches small';
  icon.setAttribute('aria-hidden', 'true');
  handle.appendChild(icon);
  wrapper.appendChild(handle);

  const clampWidth = (value) => {
    const docElement = wrapper.closest('.document') || view.dom;
    const docWidth = (docElement && docElement.clientWidth) || DEFAULT_IMAGE_MAX_WIDTH;
    const maxAllowed = Math.max(docWidth, DEFAULT_IMAGE_MAX_WIDTH);
    return Math.max(MIN_IMAGE_WIDTH, Math.min(maxAllowed, Math.round(value)));
  };

  const updateRendering = (targetNode) => {
    currentNode = targetNode;
    img.src = targetNode.attrs.src || '';
    img.alt = targetNode.attrs.alt || '';
    if (Number(targetNode.attrs.width)) {
      img.style.width = `${targetNode.attrs.width}px`;
    } else {
      img.style.width = 'auto';
    }
    img.style.maxWidth = '100%';
    img.style.height = 'auto';
  };

  updateRendering(node);

  let startX = 0;
  let startWidth = 0;

  const onPointerMove = (event) => {
    const delta = event.clientX - startX;
    const proposedWidth = clampWidth(startWidth + delta);
    const currentWidth = Number(currentNode.attrs.width) || startWidth;
    if (proposedWidth === currentWidth) return;
    const pos = getPos();
    if (typeof pos !== 'number') return;
    const tr = view.state.tr.setNodeMarkup(pos, undefined, {
      ...currentNode.attrs,
      width: proposedWidth
    });
    view.dispatch(tr);
  };

  const stopResize = () => {
    document.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerup', stopResize);
    document.removeEventListener('pointercancel', stopResize);
  };

  const startResize = (event) => {
    event.preventDefault();
    event.stopPropagation();
    startX = event.clientX;
    const bounding = img.getBoundingClientRect();
    startWidth = bounding.width || Number(currentNode.attrs.width) || MIN_IMAGE_WIDTH;
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', stopResize);
    document.addEventListener('pointercancel', stopResize);
  };

  handle.addEventListener('pointerdown', startResize);

  return {
    dom: wrapper,
    stopEvent(event) {
      return event.target === handle || handle.contains(event.target);
    },
    update(updatedNode) {
      if (updatedNode.type !== currentNode.type) return false;
      updateRendering(updatedNode);
      return true;
    },
    destroy() {
      handle.removeEventListener('pointerdown', startResize);
      stopResize();
    }
  };
};

const CustomImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      ...addNodeAnchorIdAttribute(),
      width: {
        default: null,
        parseHTML: element => element.getAttribute('width') || null,
        renderHTML: attributes => {
          if (attributes.width) {
            return { width: attributes.width };
          }
          return {};
        }
      }
    };
  },
  addNodeView() {
    return ({ node, view, getPos }) => createResizableImageNodeView(node, view, getPos);
  }
});

// Fixes a ProseMirror edge case: when the cursor is at the end of a paragraph
// whose last content is a hardBreak, pressing Delete would normally join the
// paragraph with the next block (stripping its type, e.g. converting an H1 to
// plain text). Instead we simply delete the trailing hardBreak.
const DeleteHandler = Extension.create({
  name: 'deleteHandler',

  addKeyboardShortcuts() {
    return {
      'Delete': () => {
        const { editor } = this;
        const { selection } = editor.state;

        if (!selection.empty) return false;

        const { $from } = selection;

        if ($from.parent.type.name !== 'paragraph') return false;
        if ($from.parentOffset !== $from.parent.content.size) return false;

        const lastChild = $from.parent.lastChild;
        if (!lastChild || lastChild.type.name !== 'hardBreak') return false;

        return editor.commands.deleteRange({
          from: $from.pos - lastChild.nodeSize,
          to: $from.pos,
        });
      },
    };
  },
});

const extensions = [
  StarterKit.configure({
    paragraph: false,
    heading: false,
    listItem: false,
    bulletList: false,
    orderedList: false,
    blockquote: false,
    codeBlock: false,
    image: false,
    link: {
      openOnClick: false,
      HTMLAttributes: {
        target: null,
        rel: null,
      },
      isAllowedUri: (url, context) => {
        if (/^ghost:\/\/doc\/[^/?#]+$/i.test(String(url || '').trim())) {
          return true;
        }
        if (context && typeof context.defaultValidate === 'function') {
          return context.defaultValidate(url);
        }
        return true;
      },
    },
  }),
  CustomParagraph,
  CustomHeading,
  CustomListItem,
  CustomBulletList,
  CustomOrderedList,
  CustomTaskList,
  configureExtension(CustomTaskItem, {
    nested: true,
  }),
  CustomBlockquote,
  CustomCodeBlock,
  configureExtension(CustomTable, {
    resizable: true,
    handleWidth: 4,
    cellMinWidth: 40,
    lastColumnResizable: true,
  }),
  CustomTableRow,
  CustomTableHeader,
  CustomTableCell,
  CustomImage,
  ChatMessage,
  DiffSuggestionAddMark,
  DiffSuggestionRemoveMark,
  ChunkAttribute,
  TabHandler,
  DeleteHandler,
  Placeholder.configure({
    placeholder: 'Type something here...',
  }),
];

export default extensions;
